import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Modal } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { isUndefined } from "lodash";
import * as React from "react";
import { useStore } from "zustand";

import { getG4SClient } from "../../api/g4sApi/g4sApi";
import type { Email } from "../../api/g4sApi/types/operations";
import {
    DecorationOutputType,
    type Summary,
} from "../../api/g4sApi/types/operations/createDeckDecoration";
import type { DeckDecorationLanguages } from "../../constants/deckDecoration";
import {
    DECK_DECORATION_FAILURE_MESSAGE,
    DECK_DECORATION_RESPONSE_ID_FAILURE,
    DeckDecorationType,
} from "../../constants/deckDecoration";
import type { G4sPlacement } from "../../constants/g4sPlacement";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { notificationStore, NotificationType } from "../../store/notification";
import { isLoading } from "../../utils/api.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
} from "../../utils/permission.utils";
import { logWithTakt } from "../../utils/telemetry.utils";
import { DivPadded } from "../common/styled.components";

import {
    DeckDecorationFooter,
    DeckDecorationHeaderWithLanguage,
    DeckDecorationLanguageSelector,
    DeckDecorationViewer,
} from "./deckDecoration.components";

export interface DeckDecorationData {
    email?: Email;
    summary?: Summary;
    identifier: string;
    insightType: InsightType;
    responseId: string;
}

export interface DeckDecorationWithLanguageModalProps {
    open: boolean;
    documentUrl?: string;
    identifier: string;
    insightType: InsightType;
    placement: G4sPlacement;
    handleClose: () => void;
}

export type DeckDecorationContentType = Email | Summary;

export const DeckDecorationWithLanguageModal: React.FunctionComponent<
    DeckDecorationWithLanguageModalProps
> = (props) => {
    const {
        open,
        handleClose,
        identifier,
        documentUrl,
        placement,
        insightType,
    } = props;
    const emptyDeckDecorationData: DeckDecorationData = {
        email: undefined,
        summary: undefined,
        identifier: "",
        insightType: InsightType.G4S,
        responseId: "",
    };

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [deckDecorationData, setDeckDecorationData] =
        React.useState<DeckDecorationData>(emptyDeckDecorationData);

    const [deckDecorationState, deckDecorationActions] = useAsync(async () => {
        let responseId = DECK_DECORATION_RESPONSE_ID_FAILURE;
        if (documentUrl) {
            setDeckDecorationData(emptyDeckDecorationData);
            try {
                const decorationResponse = await (
                    await getG4SClient()
                ).createDeckDecoration({
                    salesforceAccountId: getAdvertiserId()!,
                    marketplaceId: getMarketplaceId()!,
                    documentUrl,
                    outputOptions: [
                        DecorationOutputType.SUMMARY,
                        DecorationOutputType.EMAIL,
                    ],
                    placement,
                    customizationOptions: {
                        language,
                    },
                });

                responseId = decorationResponse.id;
                if (decorationResponse.status !== 200) {
                    throw new Error(decorationResponse.body.error_message);
                }
                setDeckDecorationData({
                    email: decorationResponse.body.email,
                    summary: decorationResponse.body.summary,
                    identifier,
                    insightType,
                    responseId: decorationResponse.id,
                });
            } catch (error: any) {
                console.error(error.message);
                upsertNotification(
                    DECK_DECORATION_FAILURE_MESSAGE,
                    NotificationType.ERROR,
                );
            } finally {
                logWithTakt(TELEMETRY_ID.G4S_DECK_DECORATE_PROMPT_SUBMITTED, {
                    identifier,
                    insightType,
                    responseId,
                });
            }
        }
    });

    const [language, setLanguage] = React.useState<
        DeckDecorationLanguages | undefined
    >(undefined);

    React.useEffect(() => {
        if (language) {
            void deckDecorationActions.execute();
        }
    }, [language]);

    const { email, summary, responseId } = deckDecorationData;

    const [selectedTab, setSelectedTab] = React.useState(
        DeckDecorationType.SUMMARY,
    );

    const tabContentMap: Record<
        DeckDecorationType,
        DeckDecorationContentType | undefined
    > = {
        [DeckDecorationType.EMAIL]: email,
        [DeckDecorationType.SUMMARY]: summary,
    };

    const onClose = () => {
        handleClose();
        setLanguage(undefined);
    };

    return (
        <Modal
            id="deck-decoration-modal"
            data-testid="deck-decoration-with-language-modal"
            isOpen={open}
            hasCloseButton={false}
            onClose={() => onClose()}
            padding="none"
            style={{ zIndex: 20000 }}
        >
            {isUndefined(language) ? (
                <div style={{ width: "25vw" }}>
                    <DeckDecorationLanguageSelector
                        handleClose={() => onClose()}
                        language={language}
                        setLanguage={setLanguage}
                    />
                </div>
            ) : (
                <div style={{ width: "50vw" }}>
                    <DeckDecorationHeaderWithLanguage
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        language={language}
                        setLanguage={setLanguage}
                    />
                    <DivPadded>
                        <DeckDecorationViewer
                            content={tabContentMap[selectedTab]}
                            type={selectedTab}
                            identifier={identifier}
                            insightType={insightType}
                            responseId={responseId}
                            loading={isLoading(deckDecorationState.status)}
                        />
                    </DivPadded>
                    <DeckDecorationFooter
                        identifier={identifier}
                        insightType={insightType}
                        responseId={responseId}
                        language={language}
                    />
                </div>
            )}
        </Modal>
    );
};
